/**
 * useStepRenderer.ts - Common setup hook for step renderer components
 *
 * Purpose:
 *   Provides the common data extraction, permission checks, and error handling
 *   setup that all step renderers need. This eliminates the boilerplate that
 *   was duplicated across EmailStepRenderer, TextingStepRenderer,
 *   ActionItemStepRenderer, and SwitchStepRenderer.
 *
 * Usage:
 *   const { step, template, canEdit, readonly, trigger, errorMessages } =
 *     useStepRenderer<Api.IEmailAutomationStep>(data, readOnlyProp);
 */

import * as Api from '@ViewModels';
import { useErrorMessages } from '../../../../../../models/hooks/appStateHooks';
import type { AutomationStepNodeData } from '../../../types/stepNodes';
import { useAutomationFlowContext } from '../../../contexts/AutomationFlowContext';

/**
 * Return type for useStepRenderer hook
 */
interface UseStepRendererReturn<TStep extends Api.IAutomationStep> {
	/** The automation step cast to the specific type */
	step: TStep | null;
	/** The automation template containing this step */
	template: Api.IAutomationTemplate | undefined;
	/** Whether the user can edit this step */
	canEdit: boolean;
	/** Whether this step is read-only */
	readonly: boolean;
	/** The trigger from the template (draft or published) */
	trigger: Api.IAutomationTrigger | undefined;
	/** Error messages hook for displaying API errors */
	errorMessages: ReturnType<typeof useErrorMessages>;
	/** The step data from the node */
	stepData: AutomationStepNodeData['stepData'];
	/** The step ID */
	stepId: string | undefined;
	/** Impersonation context for admin operations */
	impersonationContext: Api.IImpersonationContext | undefined;
}

/**
 * Common setup hook for step renderer components.
 *
 * Extracts step data, computes edit permissions, and provides error handling.
 * All step renderers share this same setup logic, so centralizing it here
 * eliminates duplication and ensures consistency.
 *
 * @param data - The automation step node data
 * @param readOnlyProp - Optional read-only override from props
 * @returns Common renderer setup values
 */
export function useStepRenderer<TStep extends Api.IAutomationStep>(
	data: AutomationStepNodeData,
	readOnlyProp?: boolean
): UseStepRendererReturn<TStep> {
	const errorMessages = useErrorMessages();
	const { impersonationContext } = useAutomationFlowContext();

	const { automationStep, automationTemplate, stepData, stepId } = data;
	const step = automationStep as TStep | null;
	const template = automationTemplate;

	// Get trigger from draft or published version
	const trigger = template?.draft?.trigger || template?.published?.trigger;

	// Compute edit permissions
	const canEdit = template?.status !== 'Archived' && !readOnlyProp;
	const readonly = !canEdit || !!readOnlyProp;

	return {
		step,
		template,
		canEdit,
		readonly,
		trigger,
		errorMessages,
		stepData,
		stepId,
		impersonationContext,
	};
}
